import React, { useEffect, useMemo, useRef, useState } from "react";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import "./closeDoorModal.scss";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import { debounce } from "lodash";
import { useCloseDoor } from "hooks/useLoadboard";
import EquipmentService from "app/data/equipment/equipmentServices";
import ManifestsService from "app/data/manifest/manifestsService";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { useCarrierDropdown } from "hooks/useCarrier";
import { DriverSearch } from "app/data/manifest/models";
import XGSIcons from "ui-components/icon/xgsIcons";
import XGSIcon from "ui-components/icon/xgsIcon";
import { toast } from "react-toastify";

export interface CloseDoorModalProps {
    show: boolean;
    onClose: () => void;
    closeddoordata?: any;
}

const CloseDoorModal: React.FC<CloseDoorModalProps> = ({
    show,
    onClose,
    closeddoordata
}) => {

    const loadedWeight = Number(closeddoordata?.loadedWeight) || 0;
    const MAX_CAPACITY = 42000;

    const loadPercentage = Math.min(
        Math.round((loadedWeight / MAX_CAPACITY) * 100),
        100
    );

    const equipmentService = EquipmentService.getInstance();
    const manifestService = ManifestsService.getInstance();
    const initializedRef = useRef(false);

    const [carrierFormValue, setCarrierFormValue] = useState<XGSSelectOption | null>(null);
    const [carrierSearch, setCarrierSearch] = useState<string>("");
    const [tractorFormValue, setTractorFormValue] = useState<XGSSelectOption | null>(null);
    const [driverFormValue, setDriverFormValue] = useState<XGSSelectOption | null>(null);

    const { mutate: closeDoor, isPending: isClosing } = useCloseDoor();

    const handleCloseDoor = () => {
        closeDoor(closeDoorPayload, {
            onSuccess: () => {
                toast.success("Door closed successfully.");
                onClose();
            },
            onError: (e) => {
                toast.error(`Failed to close door. ${e || ""}`);
                onClose();
            }
        });
    };

    const closeDoorPayload = useMemo(() => ({
        manifestNumber: closeddoordata?.manifestNumber,
        doorNumber: closeddoordata?.doorNumber,
        carrierNumber: carrierFormValue?.value ?? null,
        tractorNumber: tractorFormValue?.value ?? null,
        driverId: driverFormValue?.value ?? null,
    }), [
        closeddoordata,
        carrierFormValue,
        tractorFormValue,
        driverFormValue,
    ]);

    const {
        data: carrierDropdownData = [],
        isLoading: isCarrierLoading,
    } = useCarrierDropdown();

    const carrierOptions = useMemo<XGSSelectOption[]>(() => {
        return carrierDropdownData
            .filter(c => c?.carrierNumber && c?.carrierName)
            .map(c => ({
                value: String(c.carrierNumber),
                label: (
                    <span>
                        {c.carrierName}{" "}
                        <span className="text-light">{c.carrierNumber}</span>
                    </span>
                ),
                sortLabel: `${c.carrierName} ${c.carrierNumber}`,
            }))
            .sort((a, b) => a.sortLabel!.localeCompare(b.sortLabel!));
    }, [carrierDropdownData]);

    useEffect(() => {

        if (!show) {
            // Reset when modal is closed
            setCarrierFormValue(null);
            setTractorFormValue(null);
            setDriverFormValue(null);

            // Reset flag for next open
            initializedRef.current = false;
            return;
        }

        // Only populate once per open
        if (initializedRef.current) return;

        if (closeddoordata) {
            // Carrier
            if (closeddoordata.carrierNumber && carrierOptions.length) {
                const carrier = carrierOptions.find(
                    (o) => o.value === String(closeddoordata.carrierNumber)
                );
                setCarrierFormValue(carrier ?? null);
            }

            // Tractor
            if (closeddoordata.tractorNumber) {
                setTractorFormValue({
                    value: closeddoordata.tractorNumber,
                    label: closeddoordata.tractorNumber,
                });
            }

            // Driver
            if (closeddoordata.driver) {
                setDriverFormValue({
                    value: closeddoordata.driver,
                    label: closeddoordata.driver,
                });
            }

            // Mark as initialized for this open
            initializedRef.current = true;
        }
    }, [show, closeddoordata, carrierOptions]);

    //eslint-disable-next-line react-hooks/exhaustive-deps
    const loadTractorOptions = React.useCallback(
        debounce((query: string, callback: (options: XGSSelectOption[]) => void) => {
            equipmentService.getEquipmentListQuery(query, 0)
                .then(response => {
                    const options = response.data?.content.map(e => ({
                        label: e.unitNumber || "",
                        value: e.unitNumber || ""
                    })) || [];
                    callback(options);
                })
                .catch(() => {
                    callback([]);
                });
        }, 300),
        []
    );

    //eslint-disable-next-line react-hooks/exhaustive-deps
    const loadDriverOptions = React.useCallback(
        debounce((query, callback) => {
            manifestService.getDriversSearch(query)
                .then(options => callback(options.map(({ driver, searchDetails }: DriverSearch) => ({
                    value: driver.driverId,
                    label: <span dangerouslySetInnerHTML={{ __html: searchDetails.matchedStr }} />
                }))))
        }, 300),
        []
    );

    return (
        <ConfirmationModal
            opened={show}
            header={
                <div className="xgs-close-door-modal__header-wrapper">
                    <div>
                        <div className="xgs-close-door-modal__header">Close Door?</div>
                        <p className="xgs-close-door-modal__warning">
                            Once the door is closed, loading will be marked as complete.
                            You won’t be able to edit door, driver, trailer, or freight details.
                        </p>
                    </div>
                    <div className="xgs-close-door-modal__close">
                        <XGSIcon
                            icon={XGSIcons.faTimes}
                            size="lg"
                            onClick={onClose}
                        />
                    </div>
                </div>
            }
            confirmButtonText="Close Door"
            cancelButtonText="Cancel"
            onCancel={onClose}
            onConfirm={handleCloseDoor}
            disableConfirmButton={isClosing}
        >
            <div className="xgs-close-door-modal">
                <div className="xgs-close-door-modal__summary">
                    <h3 className="xgs-close-door-modal__summary-title">Load Summary</h3>

                    <div className="xgs-close-door-modal__table">
                        <div className="xgs-close-door-modal__row">
                            <span>Manifest:</span>
                            <span className="value">{closeddoordata?.manifestNumber}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Door:</span>
                            <span className="value">{closeddoordata?.doorNumber}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Origin:</span>
                            <span className="value">{closeddoordata?.originTerminalAirportCode} - {closeddoordata?.originTerminal} {closeddoordata?.originTerminalNumber}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Destination:</span>
                            <span className="value">{closeddoordata?.destinationTerminalAirportCode} - {closeddoordata?.destinationTerminal} {closeddoordata?.destinationTerminalNumber}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Load %:</span>
                            <span className="value">{loadPercentage + "%"}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Pieces:</span>
                            <span className="value">{closeddoordata?.pieces ?? 0}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Weight:</span>
                            <span className="value">{closeddoordata?.weight ?? 0} lbs</span>
                        </div>
                    </div>

                    <div className="xgs-close-door-modal__fields">
                        <LabeledSelectInput
                            name="carrier"
                            label="Carrier"
                            labelMode={LabelModes.column}
                            placeholder="Select Carrier"
                            value={carrierFormValue}
                            options={carrierOptions}
                            isLoading={isCarrierLoading}
                            inputValue={carrierSearch}
                            onInputChange={setCarrierSearch}
                            onValueChange={onChangeCarrier => setCarrierFormValue(onChangeCarrier || null)}
                            className="xgs-dispatch-load-modal__field"
                        />

                        <LabeledSelectInput
                            name="tractor"
                            label="Tractor"
                            labelMode={LabelModes.column}
                            placeholder="Start Typing..."
                            value={tractorFormValue}
                            options={[]}
                            className="xgs-dispatch-load-modal__field"
                            onValueChange={onChangeTractor => setTractorFormValue(onChangeTractor || null)}
                            loadOptions={loadTractorOptions}
                            async
                        />

                        <LabeledSelectInput
                            name="driver"
                            label="Driver"
                            labelMode={LabelModes.column}
                            placeholder="Start Typing..."
                            value={driverFormValue}
                            onValueChange={onChangeDriver => setDriverFormValue(onChangeDriver || null)}
                            loadOptions={loadDriverOptions}
                            cacheOptions
                            async
                            options={[]}
                            className="xgs-dispatch-load-modal__field"
                        />
                    </div>
                </div>
            </div>
        </ConfirmationModal>
    );
};

export default CloseDoorModal;